/**
 * TutorialCard (#124) — the shared, presentational card for one guided tour.
 *
 * Extracted so the Tutorials hub (/app/tutorials) and the onboarding Done step
 * render the SAME card and stay in sync. It's deliberately presentational and
 * knows nothing about the tour controller — that keeps it safe to render from
 * BOTH inside the app shell (where <TourProvider> is mounted) and OUTSIDE it
 * (onboarding is a sibling route of /app, so it has no TourProvider in scope).
 *
 * Start is rendered in one of two modes:
 *   - BUTTON mode  — caller passes `onStart`; clicking calls it directly. The
 *     hub uses this with `useTour().start(id)` since it lives inside the
 *     provider.
 *   - LINK mode    — no `onStart`; the card renders a <Link> to
 *     `/app/tutorials/:id`. That deep-link route (TutorialPlayerRoute) starts
 *     the tour from inside the provider, then the global <TourOverlay/> drives
 *     it over the real pages. This is what onboarding uses, because it can't
 *     call the controller from outside the provider.
 */

import { Link } from "react-router-dom";
import { CheckCircle2, Clock, GraduationCap, Play } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { cn } from "@/lib/ui/cn";
import type { TutorialDefinition } from "./definitions";

export interface TutorialCardProps {
  tutorial: TutorialDefinition;
  /** Whether this tour has been completed (drives the ✓ + Replay label). */
  done: boolean;
  /**
   * BUTTON mode: when provided, Start is a button that calls this (e.g. the hub
   * passes `() => useTour().start(id)`). When omitted, the card falls back to
   * LINK mode and Starts via a <Link> to `/app/tutorials/:id` — required for
   * callers outside <TourProvider> (onboarding).
   */
  onStart?: (id: string) => void;
}

export function TutorialCard({ tutorial, done, onStart }: TutorialCardProps) {
  const label = done ? "Replay" : "Start";

  return (
    <Card className="flex h-full flex-col gap-3 p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-md",
            done ? "bg-success/15 text-success" : "bg-brand-muted text-brand",
          )}
        >
          {done ? (
            <CheckCircle2 className="size-5" />
          ) : (
            <GraduationCap className="size-5" />
          )}
        </div>
        <span className="flex items-center gap-1 text-xs text-on-surface-muted">
          <Clock className="size-3.5" />
          {tutorial.estMin} min
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1.5">
        <h3 className="text-base font-semibold leading-tight text-on-surface">
          {tutorial.title}
        </h3>
        <p className="text-sm leading-relaxed text-on-surface-muted">
          {tutorial.summary}
        </p>
      </div>

      {onStart ? (
        <Button
          variant={done ? "outline" : "primary"}
          size="sm"
          className="mt-1 self-start"
          onClick={() => onStart(tutorial.id)}
        >
          <Play className="mr-1.5 size-4" />
          {label}
        </Button>
      ) : (
        <Button
          variant={done ? "outline" : "primary"}
          size="sm"
          className="mt-1 self-start"
          asChild
        >
          <Link to={`/app/tutorials/${tutorial.id}`}>
            <Play className="mr-1.5 size-4" />
            {label}
          </Link>
        </Button>
      )}
    </Card>
  );
}
